/* « Mon QR » — the Collaborateur's own card (v3.3 migration, pack equipe-qr: WF-117 · WF-118 · WF-119; gap-map ruling A).

   The signed-in member's personal support, read as the owner's « Carte QR de … » page reads it (routes/supports/Detail.tsx
   StaffQrPreview): the same support, the same QR drawing (@brand/qr qrSvg), the same link (lib/api supportUrl). Three states:
     · no personal support yet — « Votre QR n’a pas encore été attribué » (the responsable creates it in Équipe et caisses);
     · a support that serves no live principale today — the card says « QR indisponible pour le moment », the to-dos name
       why, and the Collaborateur contacts the responsable (she writes nothing: Manager writes, Collaborateur none);
     · a support serving a live principale — the QR, « Votre QR est prêt à être présenté », show large · save PNG · copy link.
   A test is guidance, never a gate: the QR shows whenever the support serves a live principale; « Parcours à tester » stays a
   to-do title (lib/readiness `readiness()`), nothing here hides the QR behind it. The retired sentence (« Terminez le test… »)
   is not ported. Till-facing copy says « le code », the page title « Mon QR » is the ruled menu word (lib/nav MON_QR).

   Who « me » is: the session's name (lib/scope currentUser) matched to a member of her establishments; with an API a staff
   login reads its own numbers only (lib/team `numbers`, 09 §9.1), so the member carrying numbers is the login — the seam is
   the server's member list, never a second one. The PNG is drawn here from the SVG (canvas), no server. */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { qrSvg } from '@brand/qr/qr.js'
import { Button, Card, Modal, Note, Pill, Skeleton, useToast } from '@/ui'
import { apiWrites, supportUrl, useCampaigns, useLocations, useSupports } from '@/lib/api'
import { allCampaigns, mergeSupports, servedBy, type CampaignLite, type SupportRef } from '@/lib/campaign'
import { readiness } from '@/lib/readiness'
import { currentUser } from '@/lib/scope'
import { getMembers, worksAt, type Member } from '@/lib/team'
import { personalOf } from './membres'

const CARD_LINE = 'Scannez pour découvrir le parcours du salon'
const SAME_QR = 'Les téléchargements utilisent le même QR. Changer la campagne reliée ne change pas ce code. Si le responsable remplace le QR, téléchargez à nouveau la carte.'

/** The signed-in person as a member of the team: by name among her establishments; with an API, the one row that carries numbers
    (a staff login gets its own only) when the name finds nobody. */
export function selfMember(): Member | undefined {
  const u = currentUser()
  const list = getMembers()
  const mine = list.filter(m => u.locations.length === 0 || u.locations.some(l => worksAt(m, l)))
  const byName = mine.find(m => m.firstName.trim().toLocaleLowerCase('fr-FR') === u.name.trim().toLocaleLowerCase('fr-FR'))
  if (byName) return byName
  if (apiWrites && u.role === 'staff') return mine.find(m => m.numbers != null)
  return undefined
}

/** The card as a PNG — the SVG rasterised on a canvas, the establishment, the first name and the one line under the QR. */
async function cardPng(svg: string, place: string, first: string): Promise<Blob | null> {
  const W = 900, H = 1180, Q = 620
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d'); if (!ctx) return null
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#111'; ctx.textAlign = 'center'
  ctx.font = '600 26px "Plus Jakarta Sans", Arial, sans-serif'; ctx.fillText(place.toLocaleUpperCase('fr-FR'), W / 2, 110)
  ctx.font = '500 72px Lora, Georgia, serif'; ctx.fillText(first, W / 2, 205)
  ctx.font = '400 30px "Plus Jakarta Sans", Arial, sans-serif'; ctx.fillText(`${first} · support personnel`, W / 2, 262)
  const img = new Image()
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
  try {
    await new Promise<void>((ok, ko) => { img.onload = () => ok(); img.onerror = () => ko(new Error('svg')); img.src = url })
    ctx.drawImage(img, (W - Q) / 2, 310, Q, Q)
  } catch { return null } finally { URL.revokeObjectURL(url) }
  ctx.font = '600 34px "Plus Jakarta Sans", Arial, sans-serif'; ctx.fillText(CARD_LINE, W / 2, 1010)
  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png'))
}

export default function MonQr() {
  const toast = useToast()
  const { data: places, isPending: placesPending } = useLocations()
  const { data: apiCampaigns, isPending: campaignsPending } = useCampaigns()
  const { data: apiSupports, isPending: supportsPending } = useSupports()
  const [large, setLarge] = useState(false)
  const [saving, setSaving] = useState(false)

  const names: Record<string, string> = useMemo(() => Object.fromEntries((places ?? []).map(l => [l.id, l.name])), [places])
  if (placesPending || campaignsPending || supportsPending) return (
    <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  )

  const me = selfMember()
  const first = me?.firstName ?? currentUser().name
  const supports = mergeSupports(apiSupports)
  const support: SupportRef | undefined = me ? personalOf(me, supports) : undefined
  const lite: CampaignLite[] = allCampaigns(apiCampaigns, names)
  const placeName = names[support?.locationId ?? me?.locationId ?? currentUser().locations[0] ?? ''] ?? places?.[0]?.name ?? 'votre établissement'
  const served = support ? servedBy(support, lite) : null
  const live = served?.current ?? null
  const states = support ? readiness(support, live) : []
  const todo = states.filter(s => !s.done)
  const url = support ? supportUrl(support.token) : ''
  let svg = ''
  if (support && live && support.token) { try { svg = qrSvg(url, { label: `QR personnel de ${first}` }) } catch { svg = '' } }

  const copy = () => { navigator.clipboard?.writeText(url).catch(() => {}); toast({ message: '✓ Lien copié — le même parcours que le QR, chaque participation vous est attribuée.' }) }
  const save = async () => {
    if (!svg || saving) return
    setSaving(true)
    try {
      const blob = await cardPng(svg, placeName, first)
      if (!blob) { toast({ message: 'La carte n’a pas pu être dessinée sur cet appareil. Réessayez, ou copiez le lien.' }); return }
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `QR-${first}.png`; a.click()
      setTimeout(() => URL.revokeObjectURL(a.href), 2000)
      toast({ message: '✓ Carte téléchargée — retrouvez-la dans vos téléchargements.' })
    } finally { setSaving(false) }
  }

  const head = (
    <div style={{ display: 'grid', gap: 2 }}>
      <p className="t-eyebrow" style={{ margin: 0 }}>Équipe · {placeName}</p>
      <h1 className="t-page" style={{ margin: 0 }}>Mon QR</h1>
      <p className="f-help" style={{ margin: 0 }}>Présentez votre code au client, ou gardez sa carte sur votre téléphone.</p>
    </div>
  )
  const tips = (
    <Card data-testid="mon-qr-retrouver">
      <p className="t-section" style={{ marginTop: 0 }}>Le retrouver en quelques secondes</p>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6, fontSize: 13.5 }}>
        <li><b>Gardez la carte PNG.</b> Retrouvez-la dans vos téléchargements, puis enregistrez-la dans Photos ou Fichiers selon votre téléphone.</li>
        <li><b>Gardez cette page en favori.</b> Revenez dans Mon QR pour vérifier son état et afficher la version actuelle.</li>
        <li><b>Avant de la montrer, vérifiez l’accès.</b> La carte se consulte sans connexion, mais le client a besoin d’Internet pour ouvrir le parcours.</li>
      </ol>
    </Card>
  )

  /* ---------- WF-117 (third state) · no personal support yet ---------- */
  if (!me || !support) return (
    <section data-frame="MQ-01" className="page" style={{ maxWidth: 1040 }} data-testid="mon-qr">
      {head}
      <Card data-testid="mon-qr-sans-support">
        <p className="t-section" style={{ marginTop: 0 }}>Votre QR n’a pas encore été attribué</p>
        <p className="t-body" style={{ margin: 0 }}>Le responsable crée un support personnel et vous l’attribue dans Équipe et caisses.</p>
        <p className="f-help" style={{ margin: '8px 0 0' }}>En attendant, vous validez les cadeaux à la caisse avec votre code personnel — <Link to="/caisse">Valider un cadeau</Link>.</p>
      </Card>
      {tips}
    </section>
  )

  /* ---------- WF-117 · the support serves nothing today · WF-118 · ready ---------- */
  return (
    <section data-frame="MQ-01" className="page" style={{ maxWidth: 1040 }} data-testid="mon-qr">
      {head}
      <Note tone="ok"><b>{first}</b> · le QR ouvre le parcours de {placeName} et attribue les participations à ce support.</Note>
      <Card>
        <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', alignItems: 'start' }}>
          <div data-testid="carte-qr" style={{ display: 'grid', gap: 8, justifyItems: 'center', textAlign: 'center', padding: 22, borderRadius: 14, border: '1px solid var(--bmr-border)', borderTop: '6px solid var(--bmr-action)', background: 'var(--bmr-surface)' }}>
            <p className="t-eyebrow" style={{ margin: 0 }}>{placeName}</p>
            <p className="t-display" style={{ margin: 0 }}>{first}</p>
            <p className="f-help" style={{ margin: 0 }}>{first} · support personnel</p>
            {svg
              ? <>
                  <div data-testid="mon-qr-svg" style={{ width: 220, maxWidth: '100%', lineHeight: 0, marginTop: 6 }} dangerouslySetInnerHTML={{ __html: svg }} />
                  <p style={{ margin: '4px 0 0', fontWeight: 600, fontSize: 14 }}>{CARD_LINE}</p>
                </>
              : <div style={{ display: 'grid', gap: 4, padding: '26px 18px', borderRadius: 12, background: 'var(--bmr-surface-sunken)', width: '100%', boxSizing: 'border-box' }} data-testid="qr-indisponible"><b>QR indisponible pour le moment</b></div>}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <span>{live ? <Pill tone="ok">En ligne</Pill> : <Pill tone="warn" mark="⊘">Hors ligne</Pill>}</span>
            <p className="t-section" style={{ margin: 0 }} data-testid="mon-qr-etat">{live ? 'Votre QR est prêt à être présenté' : (todo[0]?.label ?? 'Destination hors ligne')}</p>
            <p className="t-body" style={{ margin: 0 }}>Campagne&nbsp;: {live ? live.name : 'aucune en ligne aujourd’hui'}</p>
            {live ? (
              <>
                <div style={{ display: 'grid', gap: 8 }}>
                  <Button variant="primary" onClick={() => setLarge(true)} data-testid="afficher-en-grand">Afficher en grand</Button>
                  <Button disabled={saving} onClick={() => { void save() }}>Télécharger ma carte QR (PNG)</Button>
                  <Button onClick={copy}>Copier le lien client</Button>
                </div>
                {todo.length > 0 && (
                  <p className="f-help" style={{ margin: 0 }} data-testid="mon-qr-a-faire">Côté responsable, encore à faire&nbsp;: {todo.map(s => s.label).join(' · ')}. Votre QR fonctionne déjà.</p>
                )}
              </>
            ) : (
              <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bmr-surface-sunken)', display: 'grid', gap: 4 }} data-testid="mon-qr-contact">
                <span className="f-help">{todo[0]?.hint}</span>
                <b style={{ fontSize: 13.5 }}>Contactez le responsable du salon.</b>
              </div>
            )}
            <p className="f-help" style={{ margin: 0 }}>{SAME_QR}</p>
          </div>
        </div>
      </Card>
      {tips}

      {/* WF-119 · the enlarged card — high contrast, meant to be shown to the customer; not a Wallet pass */}
      <Modal open={large} title={`QR de ${first}`} onClose={() => setLarge(false)} actions={<Button onClick={() => setLarge(false)}>Fermer</Button>}>
        <div data-testid="qr-agrandi" style={{ display: 'grid', gap: 6, justifyItems: 'center', textAlign: 'center', padding: '8px 0' }}>
          <p className="t-eyebrow" style={{ margin: 0 }}>{placeName}</p>
          <p className="t-display" style={{ margin: 0 }}>{first}</p>
          <div style={{ width: 300, maxWidth: '100%', lineHeight: 0, marginTop: 6, background: '#fff', padding: 6, borderRadius: 10, boxSizing: 'border-box' }} dangerouslySetInnerHTML={{ __html: svg }} />
          <p style={{ margin: '6px 0 0', fontWeight: 600, fontSize: 15 }}>{CARD_LINE}</p>
        </div>
      </Modal>
    </section>
  )
}
